/**
 * Authentication service for Google SSO - EVOP Inspector
 */

import { User } from './userApi'
import { clearAuthCache } from '../utils/authCache'
import { debugLog, debugWarn } from '../utils/debugLog'
import { getApiUrl } from '../utils/urls'
import { getMarketForCountry } from '../constants/markets'

// Rate limit handling types and constants
interface RateLimitState {
  isRateLimited: boolean
  resetTime?: Date
  retryCount: number
  lastRetryAt?: Date
}

const RATE_LIMIT_STORAGE_KEY = 'evop_auth_rate_limit_state'
const MAX_RETRY_ATTEMPTS = 4
const INITIAL_RETRY_DELAY = 1000 // 1 second

// Circuit breaker for auth requests
class AuthCircuitBreaker {
  private failureCount = 0
  private lastFailureTime?: Date
  private readonly maxFailures = 5
  private readonly resetTimeout = 60000 // 1 minute

  canMakeRequest(): boolean {
    if (this.lastFailureTime) {
      const timeSinceLastFailure = Date.now() - this.lastFailureTime.getTime()
      if (timeSinceLastFailure > this.resetTimeout) {
        this.reset()
      }
    }
    return this.failureCount < this.maxFailures
  }

  recordSuccess(): void {
    this.reset()
  }

  recordFailure(): void {
    this.failureCount++
    this.lastFailureTime = new Date()
  }

  private reset(): void {
    this.failureCount = 0
    this.lastFailureTime = undefined
  }

  getRemainingCooldown(): number {
    if (!this.lastFailureTime || this.failureCount < this.maxFailures) {
      return 0
    }
    const elapsed = Date.now() - this.lastFailureTime.getTime()
    return Math.max(0, this.resetTimeout - elapsed)
  }
}

const circuitBreaker = new AuthCircuitBreaker()

export interface AuthStatus {
  isAuthenticated: boolean
  user?: User
  error?: string
  isRateLimited?: boolean
  rateLimitResetTime?: Date
}

// Rate limit utilities
function getRateLimitState(): RateLimitState {
  try {
    const stored = localStorage.getItem(RATE_LIMIT_STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      return {
        ...parsed,
        resetTime: parsed.resetTime ? new Date(parsed.resetTime) : undefined,
        lastRetryAt: parsed.lastRetryAt ? new Date(parsed.lastRetryAt) : undefined
      }
    }
  } catch (error) {
    debugWarn('Failed to parse rate limit state from localStorage:', error)
  }
  return {
    isRateLimited: false,
    retryCount: 0
  }
}

function setRateLimitState(state: RateLimitState): void {
  try {
    localStorage.setItem(RATE_LIMIT_STORAGE_KEY, JSON.stringify(state))
  } catch (error) {
    debugWarn('Failed to store rate limit state in localStorage:', error)
  }
}

function clearRateLimitState(): void {
  try {
    localStorage.removeItem(RATE_LIMIT_STORAGE_KEY)
  } catch (error) {
    debugWarn('Failed to clear rate limit state from localStorage:', error)
  }
}

function calculateRetryDelay(retryCount: number): number {
  return Math.min(INITIAL_RETRY_DELAY * Math.pow(2, retryCount), 30000) // Max 30 seconds
}

function canRetryNow(state: RateLimitState): boolean {
  if (!state.isRateLimited) return true
  
  const now = new Date()
  
  // Check if reset time has passed
  if (state.resetTime && now >= state.resetTime) {
    return true
  }
  
  // Check if we should retry based on exponential backoff
  if (state.lastRetryAt && state.retryCount < MAX_RETRY_ATTEMPTS) {
    const retryDelay = calculateRetryDelay(state.retryCount)
    const nextRetryTime = new Date(state.lastRetryAt.getTime() + retryDelay)
    return now >= nextRetryTime
  }
  
  return false
}

/**
 * Handle authentication callback - checks for auth tokens in URL
 * Backend sets secure HttpOnly cookie directly
 * @returns true if we're on an auth callback URL, false otherwise
 */
export function handleTokenFromUrl(): boolean {
  const hash = window.location.hash
  const params = new URLSearchParams(window.location.search)
  
  debugLog('🔍 Checking for auth callback')
  
  // Check for callback parameters (could be in hash or query params)
  const isCallback = hash.includes('auth') || 
                    params.has('code') || 
                    params.has('state') ||
                    window.location.pathname.includes('/auth/callback')
  
  if (isCallback) {
    // Clear any auth-related fragments from URL for security
    if (hash.includes('auth') || hash.includes('token=')) {
      window.location.hash = ''
      debugLog('🧹 Cleared auth fragments from URL hash for security')
    }
    
    debugLog('✅ Auth callback detected')
    return true
  }
  
  debugLog('✅ No auth callback detected')
  return false
}

/**
 * Check if user is currently authenticated with rate limit handling
 */
export async function checkAuth(): Promise<AuthStatus> {
  try {
    debugLog('🔍 Checking authentication status')
    
    // Check circuit breaker
    if (!circuitBreaker.canMakeRequest()) {
      const cooldown = circuitBreaker.getRemainingCooldown()
      debugLog('🚫 Circuit breaker active, cooldown remaining:', cooldown)
      return { 
        isAuthenticated: false, 
        error: 'Authentication temporarily unavailable. Please try again later.',
        isRateLimited: true
      }
    }
    
    // Check rate limit state
    const rateLimitState = getRateLimitState()
    if (rateLimitState.isRateLimited && !canRetryNow(rateLimitState)) {
      debugLog('⏳ Rate limited, cannot retry yet')
      return { 
        isAuthenticated: false, 
        error: 'Rate limited. Please wait before trying again.',
        isRateLimited: true,
        rateLimitResetTime: rateLimitState.resetTime
      }
    }
    
    // Use relative API path - consistent with api.ts approach
    const authUrl = `${getApiUrl()}/auth/user`
    debugLog('🌐 Making auth check request to:', authUrl)
    
    const response = await fetch(authUrl, {
      method: 'GET',
      credentials: 'include', // Important: include cookies
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    })

    debugLog('📊 Auth response status:', response.status)
    debugLog('📊 Auth response ok:', response.ok)

    if (response.ok) {
      const userData = await response.json()
      debugLog('✅ User authenticated')
      
      // Clear rate limit state on success
      clearRateLimitState()
      circuitBreaker.recordSuccess()
      
      if (userData && userData.email && userData.user_id) {
        // Map backend response to frontend User interface
        const user: User = {
          id: userData.user_id,
          email: userData.email,
          name: userData.name,
          picture: userData.picture,
          role: userData.role,
          is_active: userData.is_active !== false, // Default to true if not provided
          created_at: userData.created_at || new Date().toISOString(),
          last_login: userData.last_login,
          allowed_markets: userData.allowed_markets,
          allowed_regions: userData.allowed_regions,
          allowed_features: userData.allowed_features ?? [],
          franchise_access: userData.fp_access?.map((code: string) => ({ fp_code: code })),
        }
        debugLog('✅ Authentication successful')
        return { isAuthenticated: true, user }
      } else {
        debugLog('❌ Invalid user data in response')
      }
    } else if (response.status === 429) {
      debugLog('⏳ Rate limited (429)')
      
      // Extract reset time from headers if available
      const resetHeader = response.headers.get('X-RateLimit-Reset')
      const resetTime = resetHeader ? new Date(parseInt(resetHeader) * 1000) : undefined
      
      // Update rate limit state
      const newState: RateLimitState = {
        isRateLimited: true,
        resetTime,
        retryCount: rateLimitState.retryCount + 1,
        lastRetryAt: new Date()
      }
      setRateLimitState(newState)
      circuitBreaker.recordFailure()
      
      return { 
        isAuthenticated: false, 
        error: 'Too many requests. Please wait before trying again.',
        isRateLimited: true,
        rateLimitResetTime: resetTime
      }
    } else if (response.status === 401) {
      debugLog('🔐 User not authenticated (401)')
      // Clear rate limit on valid auth response
      clearRateLimitState()
      circuitBreaker.recordSuccess()
    } else {
      debugLog('❌ Auth request failed with status:', response.status)
      circuitBreaker.recordFailure()
    }

    return { isAuthenticated: false }
  } catch (error) {
    console.error('❌ Auth check failed with error:', error)
    circuitBreaker.recordFailure()
    return { 
      isAuthenticated: false, 
      error: 'Failed to check authentication status' 
    }
  }
}

/**
 * Manually retry authentication - clears rate limit state
 */
export async function retryAuth(): Promise<AuthStatus> {
  debugLog('🔄 Manual auth retry requested')
  clearRateLimitState()
  return await checkAuth()
}

/**
 * Get current rate limit info for UI display
 */
export function getRateLimitInfo() {
  const state = getRateLimitState()
  return {
    isRateLimited: state.isRateLimited,
    resetTime: state.resetTime,
    retryCount: state.retryCount,
    canRetryNow: canRetryNow(state),
    nextRetryIn: state.lastRetryAt && state.retryCount < MAX_RETRY_ATTEMPTS ? 
      Math.max(0, calculateRetryDelay(state.retryCount) - (Date.now() - state.lastRetryAt.getTime())) : 0
  }
}

/**
 * Initiate login flow
 */
export function login(redirectPath = '/'): void {
  // Use relative API path - consistent with api.ts approach
  const authUrl = `${getApiUrl()}/auth/login`
  const loginUrl = `${authUrl}?redirect=${encodeURIComponent(redirectPath)}`
  debugLog('🚀 Redirecting to login:', loginUrl)
  
  // Redirect to backend login endpoint
  window.location.href = loginUrl
}

/**
 * Logout user
 */
export async function logout(): Promise<void> {
  try {
    debugLog('🚪 Initiating logout')
    
    // Clear auth cache before logout
    clearAuthCache()
    
    // Use relative API path - consistent with api.ts approach
    const logoutUrl = `${getApiUrl()}/auth/logout`
    
    const response = await fetch(logoutUrl, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    })
    
    debugLog('📊 Logout response status:', response.status)
    
    // Redirect to home page regardless of response
    window.location.href = '/'
  } catch (error) {
    console.error('❌ Logout failed:', error)
    // Clear cache even on error
    clearAuthCache()
    // Still redirect to home
    window.location.href = '/'
  }
}

/**
 * Get current user info
 */
export async function getCurrentUser(): Promise<User | null> {
  try {
    debugLog('🔍 Getting current user')
    
    // Use relative API path - consistent with api.ts approach
    const userUrl = `${getApiUrl()}/auth/user`
    
    const response = await fetch(userUrl, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
    })

    debugLog('📊 getCurrentUser response status:', response.status)

    if (response.ok) {
      const userData = await response.json()
      debugLog('✅ User data retrieved')
      
      if (userData && userData.email && userData.user_id) {
        // Map backend response to frontend User interface
        const user: User = {
          id: userData.user_id,
          email: userData.email,
          name: userData.name,
          picture: userData.picture,
          role: userData.role,
          is_active: userData.is_active !== false, // Default to true if not provided
          created_at: userData.created_at || new Date().toISOString(),
          last_login: userData.last_login,
          allowed_markets: userData.allowed_markets,
          allowed_regions: userData.allowed_regions,
          allowed_features: userData.allowed_features ?? [],
          franchise_access: userData.fp_access?.map((code: string) => ({ fp_code: code })),
        }
        return user
      }
      
      return null
    }

    debugLog('🔐 getCurrentUser failed - user not authenticated')
    return null
  } catch (error) {
    console.error('❌ getCurrentUser failed:', error)
    return null
  }
}

/**
 * Check if user has admin permissions
 */
export function isAdmin(user?: User): boolean {
  return user?.role === 'EVO_ADMIN'
}

/**
 * Check if user has team-level permissions (evo_admin or evo_team)
 */
export function isTeamMember(user?: User): boolean {
  return user?.role === 'EVO_ADMIN' || user?.role === 'EVO_TEAM'
}

/**
 * Check if user has manager permissions (can access all FPs in their market)
 */
export function isManager(user?: User): boolean {
  return user?.role === 'EVO_ADMIN' || user?.role === 'EVO_TEAM'
}

/**
 * Check if user has access to a specific admin feature.
 * Only EVO_ADMIN bypasses; all other roles check allowed_features.
 */
export function hasFeatureAccess(user: User | undefined, feature: string): boolean {
  if (!user) return false
  if (isAdmin(user)) return true
  return (user.allowed_features ?? []).includes(feature)
}

/**
 * Check if user can enter the admin panel.
 * True for EVO_ADMIN, or any user with feature permissions.
 */
export function canAccessAdminPanel(user?: User): boolean {
  if (!user) return false
  if (isAdmin(user)) return true
  return (user.allowed_features ?? []).length > 0
}

/**
 * Check whether the user can reach a given franchise partner by fp_code.
 * EVO_ADMIN/EVO_TEAM see every FP; PSC_TEAM is scoped to their markets
 * (derived from the fp_code country prefix); FP_USER is limited to the
 * FPs in their franchise_access grant.
 */
export function hasAccessToFp(user: User | undefined, fpCode: string): boolean {
  if (!user || !fpCode) return false

  if (user.role === 'EVO_ADMIN' || user.role === 'EVO_TEAM') return true

  if (user.role === 'PSC_TEAM') {
    const allowedMarkets = user.allowed_markets || []
    const prefix = fpCode.slice(0, 2).toUpperCase()
    const market = getMarketForCountry(prefix)
    return market ? allowedMarkets.includes(market) : false
  }

  if (user.role === 'FP_USER') {
    const allowedFps = user.franchise_access || []
    return allowedFps.some(fa => fa.fp_code === fpCode)
  }

  return false
}

/**
 * Check if user can apply/clear the Blocked annotation on exception
 * tags. Mirrors the backend ``_role_can_block`` gate. FP_USER is
 * excluded today; flipping to include them is the single line change
 * for the future FP-side enablement.
 */
export function canBlockExceptions(user?: User): boolean {
  if (!user) return false
  return (
    user.role === 'EVO_ADMIN'
    || user.role === 'EVO_TEAM'
    || user.role === 'PSC_TEAM'
  )
}

/**
 * Check if user can see + interact with the Exception Detail Panel
 * (notes + lifecycle actions). Internal staff only; FP users are
 * 403'd at the API by ``require_non_fp_user`` and shouldn't even see
 * the notes column. Identical role set to ``canBlockExceptions``
 * today, but kept distinct so the notes rollout to FP users (if
 * we ever do it) is a one-line widen here that doesn't co-move
 * the block surface.
 */
export function canSeeExceptionNotes(user?: User): boolean {
  if (!user) return false
  return (
    user.role === 'EVO_ADMIN'
    || user.role === 'EVO_TEAM'
    || user.role === 'PSC_TEAM'
  )
}

/**
 * Gates only the drawer *trigger* (chip click / row click → open
 * ``ExceptionDrawer``). Any authenticated user, FP_USER included, can
 * open the drawer to read the summary, co-issues list, and fix-guide
 * help. The drawer's two interactive surfaces stay on their own
 * helpers: notes + composer behind ``canSeeExceptionNotes``,
 * lifecycle action buttons behind ``canBlockExceptions``. Widening
 * this gate does NOT widen those.
 */
export function canSeeExceptionDrawer(user?: User): boolean {
  return !!user
}

/**
 * Check if user can access inspector for specific FP
 * For now, all authenticated users can access inspector
 */
export function canAccessInspector(user?: User): boolean {
  return !!user && user.is_active
}